import { FunctionComponent, useEffect, useState } from 'react';
import { Heading, Tooltip } from '@primer/react';
import { Layout } from '../../components/layout/Layout';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CREATE_PATH, MINI_GAME_CREATE_PATH } from '../../utils/constants';
import { useGetConfigQuery } from '../../redux/apis/config';
import { useGetRoomsQuery } from '../../redux/apis/rooms';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import { ListRoomItem } from '../../components/gameListItem/ListRoomItem';
import littleArrow from '../../images/littleArrow.svg';
import styled from 'styled-components';
import { showModal } from '../../redux/slices/modal.slice';
import { clearGameState, getGameState } from '../../redux/slices/game.slice';
import { Room } from '../../redux/models/room.model';
import { ListEventItem } from '../../components/gameListItem/ListEventItem';
import { Bar } from '../../basicComponents/bar/Bar';
import { stopConfettiInner } from '../../components/confetti/confetti';
import { AlldoneDialog } from '../../components/plansAndBilling/AlldoneDialog';
import infobutton from '../../images/infobuttonGrey.svg';
import { InfoDialog } from '../../components/infoDialogs/InfoDialog';
import { UpgradeInfoDialog } from '../../components/infoDialogs/UpgradeInfoDialog';

const Option = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const Row = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  margin-top: 16px;
  cursor: pointer;
`;

const NormalGame = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 9999px;
  box-shadow: var(--shadow-soft);
  background: #DB004F;
`;

const MiniGame = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 9999px;
  box-shadow: var(--shadow-soft);
  background: #009B88;
`;

const SimpleGame = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 9999px;
  box-shadow: var(--shadow-soft);
  background: #FF7300;
`;

const Plus = styled('img')`
  height: 24px;
`;

const TextButton = styled('div')`
  color: #DB004F;
  font-size: 18px;
  font-weight: 600;
  line-height: normal;
  margin-left: 20px;
`;

const ScrollContainer = styled('div')`
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 20px;
`;

const Line = styled('div')`
  height: 1px;
  width: 100%;
  border-top: 1px solid #999;
`;
export const MyGamesPage: FunctionComponent = () => {
  stopConfettiInner();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [queryParams] = useSearchParams();
  const getRooms = useGetRoomsQuery(undefined);
  const getConfig = useGetConfigQuery(undefined);
  const [showNewSub, setShowNewSub] = useState('');
  const [isOpenUpgradeDialog, setIsOpenUpgradeDialog] = useState(false);

  const { room } = useAppSelector(getGameState);

  useEffect(() => {
    getRooms.refetch();
  }, []);

  useEffect(() => {
    if (room) {
      room?.connection.close();
      dispatch(clearGameState(null));
    }
  }, [room]);

  useEffect(() => {
    if (queryParams.has('plan')) {
      setShowNewSub(queryParams.get('plan')!);
    }
  }, [queryParams]);

  const onNewGame = (isEvent = false, isSimple = false) => {
    if ((getRooms.data?.length ?? 0) >= (getConfig.data?.MaxNoOfConcurrentRooms || Infinity))
      return dispatch(
        showModal({
          type: 'danger',
          message: `You can only have ${getConfig.data?.MaxNoOfConcurrentRooms} open room(s) at a time. Delete one of the rooms to create a new one.`,
        })
      );

    navigate(CREATE_PATH + `?isEvent=${isEvent}&&isSimple=${isSimple}`);
  };

  const InfoTag = ({ text, title }: { text: string, title: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
    <>
      <img
        src={infobutton}
        onClick={() => setIsOpen(true)}
        alt="Info"
        style={{
          marginTop: '15px',
          marginLeft: '15px',
          height: '23px',
        }}
      />
      { isOpen ? <InfoDialog setIsOpen={setIsOpen} text={text} title={title}/> : null }
    </>
    )
  }

  return (
    <>
      <Layout>
        <Layout.Panel style={{ maxWidth: '400px' }}>
          <Heading as="h1" sx={{ fontSize: 3, textAlign: 'center' }}>
            Games
          </Heading>
          <Heading
            as="h1"
            sx={{
              fontSize: 1,
              mb: 3,
              textAlign: 'center',
              color: '#A792FF',
              fontWeight: 400,
              textDecoration: 'underline',
            }}
          >
            <Tooltip aria-label="If open room limit is reached, delete rooms below" direction="s">
              {getRooms.data && getConfig.data
                ? `Open rooms: ${getRooms.data?.length}/${getConfig.data?.MaxNoOfConcurrentRooms}`
                : ''}
            </Tooltip>
          </Heading>

          <ScrollContainer>
            {Object.entries(groupByEvent(getRooms.data?.filter((r) => r.event) ?? [])).map(
              (event) => (
                <ListEventItem eventName={event[0]} key={event[0]} rooms={event[1]} />
              )
            )}
            {(getRooms.data?.filter((r) => !r.event) ?? []).map((room) => (
              <ListRoomItem key={room.id} room={room} />
            ))}
            {getRooms.data?.length ?? 0 > 0 ? <Line /> : null}
          </ScrollContainer>

          <Bar style={{ position: 'relative', bottom: '0px' }} />

          <Option>
            <Row onClick={() => onNewGame(getConfig.data?.EventsFeature)}>
              <NormalGame>
                <Plus alt="plus" src={littleArrow} />
              </NormalGame>
              <TextButton>The Full Experience</TextButton>
            </Row>
            <InfoTag title="The Full Experience" text="45+ mins: Travel around a game board for the full Cozy Juicy Real journey and connect deeply." />
          </Option>
          <Option>
            <Row style={{ opacity: getConfig.data?.SimpleGameFeature ? 1 : 0.5 }}
              onClick={() => getConfig.data?.SimpleGameFeature ? onNewGame(true, true) : setIsOpenUpgradeDialog(true)}>
              <SimpleGame>
                <Plus alt="arrowIcon" src={littleArrow} />
              </SimpleGame>
              <TextButton style={{ color: '#FF7300' }}>Quick Connect Game</TextButton>
            </Row>
            <InfoTag title="Quick Connect Game" text="20+ mins: Engaging questions, playful activities and interactive Spark cards – without a game board." />
          </Option>
          <Option>
            <Row style={{ opacity: getConfig.data?.CheckInQuestionsFeature ? 1 : 0.5 }}
              onClick={() => getConfig.data?.CheckInQuestionsFeature ? navigate(MINI_GAME_CREATE_PATH) : setIsOpenUpgradeDialog(true)}>
              <MiniGame>
                <Plus alt="arrowIcon" src={littleArrow} />
              </MiniGame>
              <TextButton style={{ color: '#009B88' }}>Check-in questions</TextButton>
            </Row>
            <InfoTag title="Check-in questions" text="10+ mins: Keep it simple with Cozy, Juicy & Real questions – ideal for kicking of meetings meaningfully." />
          </Option>
        </Layout.Panel>
      </Layout>

      <AlldoneDialog
        open={!!showNewSub}
        planName={showNewSub}
        setOpen={(b: boolean) => setShowNewSub(b ? 'plan' : '')}
      />
      <UpgradeInfoDialog setConfirm={setIsOpenUpgradeDialog} confirm={isOpenUpgradeDialog} />
    </>
  );
};

function groupByEvent(rooms: Room[]) {
  const groupedObjects: { [key: string]: Room[] } = {};

  for (const object of rooms) {
    const event = object.event;

    if (!groupedObjects[event]) {
      groupedObjects[event] = [];
    }

    groupedObjects[event].push(object);
  }

  return groupedObjects;
}
